import { Action, action, Thunk, thunk } from 'easy-peasy'
import {
    addMoveToGTPosition,
    createRootGameTree,
    getMoveById,
    getNextPosition,
    getPositionById,
    getPreviousPosition,
    getStartingPosition,
} from '../../chess/gameTree'
import { createPuzzlePGNString, gameFromPGN } from '../../chess/pgn'
import { BasePositionFEN } from '../../chess/positionPresets'
import { Color, GameTree, GameTreePosition, Move } from '../../chess/types'
import { Arrow, Square } from '../../react-chessboard/src/chessboard/types'
import { analyticsManager } from '../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { ITag, PuzzleStatus, usedHint } from '../PuzzlesView/functions/puzzlesApi'
import { getPuzzleQuest, resolvePuzzle } from './API'
import { getReward } from './common'
import { AUTO_FEED_TIMEOUT, AUTO_MOVE_BASE, AUTO_MOVE_EXTENDED, getAutoMoveDelay, STEP_PROGRESS } from './timing'
import { QuestPuzzle, StepProgress } from './types'

export const autoFeedTimeout = AUTO_FEED_TIMEOUT
export const autoMoveTimeout = AUTO_MOVE_BASE

export interface PuzzleQuestModel {
    puzzleId: string
    colorToMove: number
    currentPosition: GameTreePosition
    flipped: boolean
    gameTree: GameTree
    myColor: Color
    puzzle?: QuestPuzzle['puzzle']
    nextPuzzle?: QuestPuzzle
    currentPuzzleNumber: number
    stepProgress: StepProgress
    cycleProgress: QuestPuzzle['cycle']
    userPuzzleRating: number
    feedbackMessage: string
    feedbackGroupId: number
    // local state
    moveStatus?: boolean
    hint?: Square
    hintsCount: number
    availableHints: number
    customArrows: Array<Arrow>
    showSolution: boolean
    showingSolution: boolean
    puzzleState: boolean | null
    autoMoveTimeout?: NodeJS.Timeout
    resultsPopup: boolean
    showHintLimit: boolean

    // Actions
    resetGame: Action<PuzzleQuestModel>
    savePuzzle: Action<PuzzleQuestModel, QuestPuzzle>
    setResultsPopup: Action<PuzzleQuestModel, boolean>
    setCurrentPosition: Action<PuzzleQuestModel, GameTreePosition>
    flipBoard: Action<PuzzleQuestModel>
    setNextPuzzle: Action<PuzzleQuestModel, QuestPuzzle>
    setStepProgress: Action<PuzzleQuestModel, QuestPuzzle['step']>
    setCycleProgress: Action<PuzzleQuestModel, QuestPuzzle['cycle']>
    setFeedbackMessage: Action<PuzzleQuestModel, string>
    setFeedbackGroupId: Action<PuzzleQuestModel, number>
    // Local Actions
    setMoveStatus: Action<PuzzleQuestModel, boolean | undefined>
    setHint: Action<PuzzleQuestModel, { from: Square; to?: Square } | undefined>
    setShowSolution: Action<PuzzleQuestModel, boolean>
    setShowingSolution: Action<PuzzleQuestModel, boolean>
    setPuzzleState: Action<PuzzleQuestModel, boolean | null>
    setAutoMoveTimeout: Action<PuzzleQuestModel, NodeJS.Timeout>
    setAvailableHints: Action<PuzzleQuestModel, number>
    setShowHintLimit: Action<PuzzleQuestModel, boolean>

    autoMove: Thunk<PuzzleQuestModel>
    showRightSolution: Thunk<PuzzleQuestModel>
    handleMove: Thunk<PuzzleQuestModel, Move>
    asyncSavePuzzle: Thunk<PuzzleQuestModel, QuestPuzzle>

    // API
    getPuzzle: Thunk<PuzzleQuestModel>
    finishPuzzle: Thunk<PuzzleQuestModel, { id: string; status: PuzzleStatus }>
    setHintAsync: Thunk<PuzzleQuestModel, { from: Square; to?: Square } | undefined>
}

const initialGameTree = createRootGameTree(BasePositionFEN)
const initialCurrentPosition = getStartingPosition(initialGameTree)

export const PuzzleQuestViewModel: PuzzleQuestModel = {
    stepProgress: {
        completed: false,
        endTime: '',
        progress: [],
        progressThreshold: [],
        reward: 0,
        isLimitReached: false,
        hints: 0,
    },
    cycleProgress: {
        rewards: [],
        rewardsTotal: 0,
        rewardsThreshold: [],
        endTime: '',
        season: '',
        stepsToGo: 0,
    },
    puzzle: undefined,
    userPuzzleRating: 0,
    feedbackMessage: '',
    feedbackGroupId: 0,
    moveStatus: false,
    hint: undefined,
    hintsCount: 0,
    availableHints: -1,
    customArrows: [],
    showSolution: false,
    showingSolution: false,
    puzzleState: null,
    currentPuzzleNumber: 1,
    nextPuzzle: undefined,
    puzzleId: '',
    autoMoveTimeout: undefined,
    myColor: Color.White,
    colorToMove: Color.White,
    flipped: false,
    gameTree: initialGameTree,
    currentPosition: initialCurrentPosition,
    resultsPopup: false,
    showHintLimit: false,

    // ACTIONS
    setMoveStatus: action((state, payload) => {
        state.moveStatus = payload
    }),
    setHint: action((state, payload) => {
        if (payload) {
            state.hint = payload.from
            state.hintsCount += 1
            if (payload.to) {
                state.customArrows.push([payload.from, payload.to, '#C9F1AD', 0.8])
            }
        } else {
            state.hint = undefined
            state.customArrows = []
        }
    }),
    setShowSolution: action((state, payload) => {
        state.showSolution = payload
    }),
    setShowingSolution: action((state, payload) => {
        state.showingSolution = payload
    }),
    setPuzzleState: action((state, payload) => {
        state.puzzleState = payload
    }),

    resetGame: action((state) => {
        state.myColor = Color.White
        state.gameTree = initialGameTree
        state.currentPosition = initialCurrentPosition
        state.colorToMove = 1
    }),

    flipBoard: action((state) => {
        state.flipped = !state.flipped
    }),

    savePuzzle: action((state, payload) => {
        if (!payload.puzzle || payload.puzzle.id === state.puzzleId) return
        const puzzlePGNstring = createPuzzlePGNString(payload.puzzle.startingFen, payload.puzzle?.moves)
        const gameTreeFromPGN = gameFromPGN(puzzlePGNstring)
        const startingPosition = getStartingPosition(gameTreeFromPGN)

        clearTimeout(state.autoMoveTimeout)
        state.hintsCount = 0
        state.puzzleState = null
        state.hint = undefined
        state.moveStatus = undefined
        state.showSolution = false

        // first load

        state.resultsPopup = payload.cycle?.stepsToGo === 1 ? Boolean(payload.step?.completed) : false

        // if it's the first move, set the color to move to the color of the starting position
        if (payload.puzzle?.isFirstMove) {
            state.myColor = startingPosition.position.turn
            state.flipped = startingPosition.position.turn === Color.Black
        } else {
            const color = startingPosition.position.turn === Color.White ? Color.Black : Color.White
            state.myColor = color
            state.flipped = color === Color.Black
        }

        state.puzzle = payload.puzzle
        state.cycleProgress = payload.cycle
        payload.step && (state.currentPuzzleNumber = payload.step.progress.length + 1)
        state.nextPuzzle = undefined
        state.puzzleId = payload.puzzle?.id || ''
        state.userPuzzleRating = payload.userPuzzleRating
        state.feedbackMessage = payload.feedbackMessage || ''
        state.feedbackGroupId = payload.feedbackGroupId || 0

        if (payload.step?.completed) {
            state.gameTree = initialGameTree
            state.currentPosition = getStartingPosition(initialGameTree)
        } else {
            state.gameTree = gameTreeFromPGN
            state.currentPosition = startingPosition
        }

        if (payload.options) {
            state.availableHints = payload.options.availableHints
        }

        payload.step && (state.stepProgress = payload.step)

        // ANALYTICS
        if (payload.puzzle) {
            analyticsManager.dispatchEvent('startedPuzzle', {
                puzzleId: payload.puzzle.id,
                puzzleRating: payload.puzzle.rating,
                puzzleTags: payload.puzzle.tags ? payload.puzzle.tags.map((tag: ITag) => tag.name).join(', ') : '',
                origin: 'dailyPuzzle',
                userPuzzleRating: payload.userPuzzleRating,
            })
        }
        // ############
    }),

    setResultsPopup: action((state, payload) => {
        state.resultsPopup = payload
    }),

    setCurrentPosition: action((state, payload) => {
        state.currentPosition = payload
    }),

    setNextPuzzle: action((state, payload) => {
        state.nextPuzzle = payload
    }),

    setStepProgress: action((state, payload) => {
        if (payload) {
            state.stepProgress = {
                ...payload,
                progress: [...payload.progress], // Create new array reference
            }
        }
    }),
    setCycleProgress: action((state, payload) => {
        payload && (state.cycleProgress = payload)
    }),
    setFeedbackMessage: action((state, payload) => {
        state.feedbackMessage = payload
    }),
    setFeedbackGroupId: action((state, payload) => {
        state.feedbackGroupId = payload
    }),
    setAutoMoveTimeout: action((state, payload) => {
        state.autoMoveTimeout = payload
    }),
    setAvailableHints: action((state, payload) => {
        state.availableHints = payload
    }),
    setShowHintLimit: action((state, payload) => {
        state.showHintLimit = payload
    }),

    autoMove: thunk((actions, payload, { getState }) => {
        const state = getState()

        const moveIndex = state.gameTree.moves.findIndex((move) => move.previousPositionId === state.currentPosition.id)
        const timeout = setTimeout(
            () => {
                actions.setMoveStatus(undefined)
                const nextPosition = getNextPosition(state.gameTree, state.currentPosition.id)
                if (nextPosition) {
                    actions.setCurrentPosition(nextPosition)
                } else {
                    actions.setShowingSolution(false)
                    if (!state.nextPuzzle) {
                        actions.finishPuzzle({ id: state.puzzleId, status: PuzzleStatus.SHOWED })
                        actions.setPuzzleState(false)
                    }
                }
            },
            getAutoMoveDelay(moveIndex),
        )

        actions.setAutoMoveTimeout(timeout)
    }),

    showRightSolution: thunk((actions, payload, { getState }) => {
        const state = getState()

        const previousPosition = getPreviousPosition(state.gameTree, state.currentPosition.id)
        if (previousPosition) {
            actions.setMoveStatus(undefined)
            actions.setCurrentPosition(previousPosition)
            actions.setShowingSolution(true)
            actions.setShowSolution(true)
        }
    }),

    handleMove: thunk((actions, move, { getState }) => {
        const state = getState()
        actions.setHint(undefined)
        // if (gameTreePosition.position.turn !== myColor) return false
        const currentProgressPosition = getPositionById(state.gameTree, state.currentPosition.id)
        const expectedProgressMove = getMoveById(state.gameTree, currentProgressPosition?.nextMoveIds[0])
        if (!expectedProgressMove) return false
        const { from: expectedFrom, to: expectedTo } = expectedProgressMove.move
        const { from, to } = move

        if (from === expectedFrom && to === expectedTo) {
            actions.setMoveStatus(true)

            const nextPosition = getNextPosition(state.gameTree, state.currentPosition.id)
            nextPosition && actions.setCurrentPosition(nextPosition)

            const nextBotPosition = getPositionById(state.gameTree, expectedProgressMove.nextPositionId)
            if (nextBotPosition.nextMoveIds.length === 0) {
                actions.setPuzzleState(true)
                actions.finishPuzzle({ id: state.puzzleId, status: PuzzleStatus.PASSED })
            }
            return true
        } else {
            actions.setMoveStatus(false)
            actions.setPuzzleState(false)

            const nextPosition = addMoveToGTPosition(state.gameTree, state.currentPosition, move)
            actions.setCurrentPosition(nextPosition)
            actions.finishPuzzle({ id: state.puzzleId, status: PuzzleStatus.FAILED })

            return false
        }
    }),

    asyncSavePuzzle: thunk(async (actions, payload, helpers) => {
        clearTimeout(helpers.getState().autoMoveTimeout)

        actions.setFeedbackMessage(payload.feedbackMessage || '')
        actions.setFeedbackGroupId(payload.feedbackGroupId || 0)
        if (payload?.step?.completed) {
            // ANALYTICS
            const reward = getReward(payload?.cycle)
            const prevReward = getReward(helpers.getState().cycleProgress)
            analyticsManager.dispatchEvent('reward', {
                type: 'crown',
                tier: 0,
                amount: payload?.step?.reward || 0,
                expirationDate: payload?.cycle?.endTime || '',
            })

            if (reward && reward !== prevReward) {
                analyticsManager.dispatchEvent('reward', {
                    type: 'frame',
                    tier: reward,
                    amount: 1,
                    expirationDate: payload?.cycle?.endTime || '',
                })
            }
            // ############

            actions.setStepProgress({ ...payload.step, completed: false })

            let stepProgressTimeout
            let cycleProgressTimeout
            const interval = setInterval(() => {
                const showingSolution = helpers.getState().showingSolution
                if (showingSolution) {
                    clearTimeout(stepProgressTimeout)
                    clearTimeout(cycleProgressTimeout)
                    stepProgressTimeout = undefined
                    cycleProgressTimeout = undefined
                } else {
                    if (!stepProgressTimeout) {
                        stepProgressTimeout = setTimeout(() => {
                            clearInterval(interval)
                            actions.setStepProgress(payload.step)
                            setTimeout(() => {
                                actions.setResultsPopup(true)
                            }, STEP_PROGRESS.resultsPopupDelay)
                            cycleProgressTimeout = setTimeout(() => {
                                actions.setCycleProgress(payload.cycle)
                            }, STEP_PROGRESS.cycleProgressDelay)
                        }, STEP_PROGRESS.mainTimeout)
                    }
                }
            }, STEP_PROGRESS.pollingInterval)
        } else {
            actions.setStepProgress(payload.step)
        }
    }),

    // API
    getPuzzle: thunk(async (actions) => {
        const data = await getPuzzleQuest()
        if (!data) return
        actions.savePuzzle(data)
    }),

    finishPuzzle: thunk(async (actions, payload, helpers) => {
        const currentState = helpers.getState()
        const data = await resolvePuzzle({
            puzzleId: payload.id,
            status: payload.status,
            usedHints: currentState.hintsCount,
        })

        if (!data) {
            actions.getPuzzle()
            console.error('Failed to resolve puzzle')
            return
        }

        // ANALYTICS
        analyticsManager.dispatchEvent('completedPuzzle', {
            origin: 'puzzleQuest',
            result: payload.status,
            attempts: 1,
            usedHints: currentState.hintsCount,
            puzzleRating: currentState.puzzle?.rating || 0,
            userPuzzleRating: currentState.userPuzzleRating,
            puzzleId: payload.id,
            puzzleSource: 0,
            datePlayed: new Date().toISOString(), // GTM expects this parameter
        })
        // #########

        !data.step?.completed && actions.setNextPuzzle(data)
        actions.asyncSavePuzzle(data)
    }),

    setHintAsync: thunk(async (actions, payload, helpers) => {
        try {
            const { getState } = helpers
            const state = getState()
            if (state.availableHints !== 0) {
                const result = await usedHint()
                actions.setHint(payload)
                actions.setAvailableHints(result.availableHints)
                analyticsManager.dispatchEvent('powerUpUsed', {
                    type: 'hints',
                    oldArgument: state.availableHints,
                    newArgument: result.availableHints,
                    argumentChange: state.availableHints - result.availableHints,
                    feature: 'puzzleQuest',
                    puzzleId: state.puzzleId,
                })
            }
        } catch (error) {
            console.error('[PUZZLE_QUEST] Error in setHintAsync:', error)
        }
    }),
}
